<template>
  <div class="backgroundColor pa-6" style="min-height: 100vh;">
    <header class="mb-6 d-flex align-center">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            color="grey darken-1"
            class="px-0 mr-4"
            @click="volverBandeja"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
        </template>
        <span>Volver a la Gestión de Entregas</span>
      </v-tooltip>

      <h1 class="text-h4 font-weight-bold primary--text mb-0 mr-4">
        Venta: <span class="font-weight-regular">{{ folioVenta }}</span>
      </h1>

      <v-chip small dark :color="chipColor" class="font-weight-medium">
        {{ currentStatus }}
      </v-chip>
    </header>
    <v-row>
      <v-col cols="12" md="8">
        <v-card outlined class="pa-5 mb-6">
          <div class="d-flex align-center mb-2">
            <h2 class="text-h6 font-weight-bold primary--text mb-0">
              <v-icon color="primary" left>mdi-package-variant-closed-check</v-icon>
              Mercancía Lista para Entregar
            </h2>
          </div>
          <p class="grey--text text--darken-1 mb-4 text-body-2">
            Valida físicamente las piezas disponibles en mostrador antes de confirmar la salida.
          </p>

          <v-simple-table v-if="dispatchableItems.length > 0" class="elevation-0">
            <template v-slot:default>

              <!-- CABECERA -->
              <thead class="grey lighten-5">
                <tr>
                  <th class="text-left text-uppercase font-weight-bold grey--text text--darken-2">
                    Producto
                  </th>
                  <!-- Anchos fijos inamovibles -->
                  <th class="text-center text-uppercase font-weight-bold grey--text text--darken-2" style="width: 110px;">
                    En Tienda
                  </th>
                  <th class="text-center text-uppercase font-weight-bold grey--text text--darken-2" style="width: 160px;">
                    A Despachar
                  </th>
                  <th class="text-center text-uppercase font-weight-bold grey--text text--darken-2" style="width: 80px;">
                    Estado
                  </th>
                </tr>
              </thead>

              <!-- CUERPO DE LA TABLA -->
              <tbody>
                <tr
                  v-for="item in dispatchableItems"
                  :key="`${item.product_id}-${item.delivery_status_id}`"
                  :class="{
                    'green lighten-5': item.validatedCount === item.pending,
                  }"
                  class="transition-swing"
                >
                  <!-- COLUMNA 1: PRODUCTO -->
                  <td class="py-3" style="max-width: 250px;">
                    <!-- Nombre Principal -->
                    <div class="text-truncate font-weight-bold text-body-2 text-slate-800" :title="item.name">
                      {{ item.name }}
                    </div>
                    <!-- Metadatos (SKU > Marca > Modelo) -->
                    <div class="text-truncate text-caption grey--text text--darken-1 mt-1">
                      <span class="font-weight-black black--text">{{ item.sku }}</span>
                      <span class="mx-1">&bull;</span> {{ item.brand }}
                      <span class="mx-1">&bull;</span> {{ item.model }}
                    </div>
                  </td>

                  <!-- COLUMNA 2: PENDIENTE -->
                  <td class="text-center font-weight-bold text-body-2">
                    {{ item.pending }} pz
                  </td>

                  <!-- COLUMNA 3: STEPPER DE CONTEO -->
                  <td class="text-center px-1">
                    <div class="d-flex align-center justify-center mx-auto" style="max-width: 160px;">
                      <v-btn
                        icon
                        small
                        color="grey darken-1"
                        @click="decrementDispatchable(item)"
                        :disabled="item.validatedCount <= 0 || isEntregado"
                      >
                        <v-icon>mdi-minus</v-icon>
                      </v-btn>

                      <v-text-field
                        v-model.number="item.validatedCount"
                        type="number"
                        dense
                        hide-details="auto"
                        outlined
                        :disabled="isEntregado"
                        class="custom-number-input mx-1"
                        style="max-width: 90px;"
                        @input="validateDispatchableInput(item)"
                        @blur="handleDispatchableBlur(item)"
                      ></v-text-field>

                      <v-btn
                        icon
                        small
                        color="grey darken-1"
                        @click="incrementDispatchable(item)"
                        :disabled="item.validatedCount >= item.pending || isEntregado"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </div>
                  </td>

                  <!-- COLUMNA 4: ESTADO -->
                  <td class="text-center">
                    <v-icon v-if="item.validatedCount === item.pending" color="success">
                      mdi-package-variant-closed-check
                    </v-icon>
                    <v-icon v-else color="grey lighten-1">
                      mdi-package-variant-closed
                    </v-icon>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- MENSAJE SI NO HAY MERCANCÍA LISTA -->
          <v-card-text v-else class="text-center grey--text py-6">
            <v-icon size="40" color="grey lighten-1" class="mb-2">mdi-package-variant</v-icon>
            <div class="subtitle-1">No hay artículos listos para entrega inmediata en esta sucursal.</div>
          </v-card-text>
          </v-card>

        <!-- =================================================== -->
        <!-- TABLA 2: INFORMATIVA (PRODUCTOS NO DISPONIBLES)    -->
        <!-- =================================================== -->
        <v-card v-if="nonAvailableItems.length > 0" outlined class="pa-5 lighten-4">
          <div class="d-flex align-center mb-2">
            <h2 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-0">
              <v-icon color="warning" left>mdi-clock-alert-outline</v-icon>
              Mercancía Asignada a Otras Sucursales o en Tránsito
            </h2>
          </div>
          <p class="caption grey--text text--darken-1 mb-4 text-body-2">
            Estos artículos estan pendientes de surtir o en tránsito hacia la sucursal.
          </p>

          <v-simple-table class="elevation-0 grey lighten-4">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold grey--text text--darken-2">
                    Producto
                  </th>
                  <th class="text-center text-uppercase caption font-weight-bold grey--text text--darken-2" style="width: 110px;">
                    Faltantes
                  </th>
                  <th class="text-center text-uppercase caption font-weight-bold grey--text text--darken-2">
                    Sucursal Destino
                  </th>
                  <th class="text-center text-uppercase caption font-weight-bold grey--text text--darken-2" style="width: 140px;">
                    Estatus
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in nonAvailableItems" :key="`${item.product_id}-${item.delivery_status_id}`">
                  <td class="py-2">
                    <div class="caption font-weight-bold grey--text text--darken-3">{{ item.name }}</div>
                    <div class="caption grey--text">{{ item.sku }} &bull; {{ item.brand }} &bull; {{ item.model }}</div>
                  </td>
                  <td class="text-center caption font-weight-bold grey--text text--darken-2">
                    {{ item.pending }} pz
                  </td>
                  <td class="text-center">
                    {{ item.pickup_store_name }}
                  </td>
                  <td class="text-center">
                    <div class="d-flex align-center justify-center">
                      <v-chip x-small dark :color="getItemStatusColor(item.delivery_status_id)">
                        {{ getItemStatusText(item.delivery_status_id) }}
                      </v-chip>
                    </div>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>

        <!-- =================================================== -->
        <!-- TABLA 3: PRODUCTOS YA ENTREGADOS PREVIAMENTE       -->
        <!-- =================================================== -->
        <v-card v-if="deliveredItems.length > 0" outlined class="pa-5 mt-6 lighten-5">
          <div class="d-flex align-center mb-2">
            <h2 class="text-subtitle-1 font-weight-bold teal--text text--darken-3 mb-0">
              <v-icon color="teal darken-2" left>mdi-package-variant-closed-check</v-icon>
              Mercancía Entregada Previamente
            </h2>
          </div>
          <p class="caption grey--text text--darken-1 mb-4 text-body-2">
            Artículos retirados por el cliente en visitas anteriores registradas en bitácora.
          </p>

          <v-simple-table class="elevation-0 white">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left text-uppercase caption font-weight-bold grey--text text--darken-2">
                    Producto
                  </th>
                  <th class="text-center text-uppercase caption font-weight-bold grey--text text--darken-2" style="width: 120px;">
                    Piezas Entregadas
                  </th>
                  <th class="text-center text-uppercase caption font-weight-bold grey--text text--darken-2" style="width: 130px;">
                    Estatus
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in deliveredItems"
                  :key="`${item.product_id}-${item.delivery_status_id}`"
                  class="teal lighten-5">
                  <td class="py-2">
                    <div class="caption font-weight-bold grey--text text--darken-4">
                      {{ item.name }}
                    </div>
                    <div class="caption grey--text text--darken-2">
                      <span class="font-weight-bold">{{ item.sku }}</span> &bull; {{ item.brand }} &bull; {{ item.model }}
                    </div>
                  </td>
                  <td class="text-center caption font-weight-bold teal--text text--darken-4">
                    {{ item.pending }} pz
                  </td>
                  <td class="text-center">
                    <v-chip x-small dark color="teal darken-1" class="font-weight-medium">
                      <v-icon x-small left>mdi-check-all</v-icon>
                      Entregado
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card outlined class="pa-5 mb-4">
          <div class="d-flex align-center mb-4">
            <h2 class="text-h6 font-weight-bold primary--text mb-0">Protocolo de Salida</h2>
          </div>

          <v-card color="grey lighten-4" elevation="0" class="pa-3 mb-4">
            <div class="text-body-2 mb-1">
              <span class="font-weight-bold grey--text text--darken-2">Cliente:</span>
              {{ customer.name }}
            </div>
            <div class="text-body-2">
              <span class="font-weight-bold grey--text text--darken-2">Contacto:</span>
              {{ customer.phone }}
            </div>
            <div class="text-body-2">
              <span class="font-weight-bold grey--text text--darken-2">Email:</span>
              {{ customer.email }}
            </div>
          </v-card>

          <v-divider class="mb-4"></v-divider>

          <v-checkbox
            v-model="isValidatedID"
            :disabled="isEntregado"
            color="secondary"
            class="mt-0"
            hide-details
          >
            <template v-slot:label>
              <span class="text-body-2 text--primary">
                Validé Identificación Oficial (INE / Pasaporte) <span class="error--text">*</span>
              </span>
            </template>
          </v-checkbox>

          <v-text-field
            v-model="documentFolio"
            :disabled="isEntregado"
            outlined
            dense
            label="Número de Identificación (Folio)"
            placeholder="Ej. IDMEX12345..."
            class="mb-4 mt-3"
            hide-details="auto"
            :rules="[v => !!v || 'El folio es obligatorio para entregar']"
          ></v-text-field>

          <v-textarea
            v-model="notes"
            :disabled="isEntregado"
            outlined
            dense
            label="Notas o Incidencias de Despacho"
            placeholder="Escribe comentarios sobre el empaque, lote..."
            rows="3"
            hide-details
          ></v-textarea>
        </v-card>

        <v-btn
          @click="confirmDispatch"
          :disabled="!canConfirm || isEntregado"
          :loading="isConfirming"
          color="secondary"
          block
          large
          elevation="2"
        >
          <v-icon left>mdi-check</v-icon>
          Confirmar Entrega
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ClientDeliveryService from '@/services/ClientDeliveryService';
import Swal from 'sweetalert2';

export default {
  name: 'DespachoMercancia',

  data() {
    return {
      isLoading: true,
      isConfirming: false,
      isEntregado: false,
      isValidatedID: false,
      documentFolio: '',
      notes: '',
      customer: { name: '', phone: '', email: '' },
      items: [],
      // Guardaremos la respuesta del backend aquí
      orderData: null,
    };
  },
  async mounted() {
    await this.fetchOrderDetails();
  },

  computed: {
    folioVenta() {
      return this.$route.params.folio || 'N/A';
    },
    currentStatus() {
      if (this.isEntregado) return 'Entregado';
      return this.orderData?.status_description || 'Desconocido';
    },
    // Nueva propiedad para controlar el color del v-chip
    chipColor() {
      if (this.isEntregado) return 'success'; // Verde al finalizar

      const statusId = this.orderData?.status_id;
      if (statusId === 4) return 'lightPrimary';
      if (statusId === 3) return 'warning';
      return 'error';
    },
    dispatchableItems() {
      return this.items.filter((item) => item.delivery_status_id === 4 && item.can_deliver_here); // READY_IN_STORE
    },
    nonAvailableItems() {
      // Estatus 2 (PENDING) y 3 (IN_TRANSIT)
      // return this.items.filter((item) => item.delivery_status_id === 2 || item.delivery_status_id === 3);
      return this.items.filter((item) => item.delivery_status_id === 2 || item.delivery_status_id === 3 || (item.delivery_status_id === 4 && !item.can_deliver_here));
    },
    // Nueva propiedad computed para la tabla de entregados
    deliveredItems() {
      return this.items.filter((item) => item.delivery_status_id === 5 || item.delivery_status_id === 1); // DELIVERED
    },
    canConfirm() {
      if (!this.isValidatedID) return false;
      if (!this.documentFolio.trim()) return false;
      if (this.dispatchableItems.length === 0) return false;

      const esValido = this.dispatchableItems.every((item) => item.validatedCount >= 0
        && item.validatedCount <= item.pending);

      const totalToDispatch = this.dispatchableItems.reduce((sum, item) => sum + (item.validatedCount || 0), 0);

      return esValido && totalToDispatch > 0;
    },
  },
  methods: {
    getItemStatusColor(statusId) {
      switch (statusId) {
        case 1: return 'teal darken-1'; // DELIVERED
        case 2: return 'orange darken-1'; // PENDING
        case 3: return 'blue'; // IN_TRANSIT
        case 4: return 'success'; // READY_IN_STORE
        case 5: return 'teal darken-1'; // DELIVERED
        default: return 'grey';
      }
    },
    getItemStatusText(statusId) {
      switch (statusId) {
        case 1: return 'Entregado';
        case 2: return 'Pendiente';
        case 3: return 'En Tránsito';
        case 4: return 'Listo en Tienda';
        case 5: return 'Entregado';
        default: return 'N/A';
      }
    },
    async fetchOrderDetails() {
      this.isLoading = true;
      try {
        const { folio } = this.$route.params;
        const storeId = this.$store.getters.currentStoreId;
        const response = await ClientDeliveryService.getOrderDetails(folio, storeId, false);

        if (response.status === 'success') {
          this.orderData = response.data;
          this.customer = this.orderData.customer || { name: 'Desconocido', phone: 'N/A', email: 'N/A' };
          this.items = (this.orderData.items || []).map((item) => ({
            ...item,
            validatedCount: 0,
          }));
          // this.orderData.status_id = parseInt(this.$route.query.status_id, 10) || 2;
          // this.orderData.status_description = this.$route.query.status_desc || 'Pendiente';
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Orden no encontrada',
          text: 'No se pudieron cargar los productos de esta orden.',
          confirmButtonColor: 'primary',
        }).then(() => {
          this.volverBandeja();
        });
      } finally {
        this.isLoading = false;
      }
    },
    // Métodos para interactuar específicamente con ítems de la tabla despachable
    incrementDispatchable(item) {
      if (item.validatedCount < item.pending) {
        // Encontrar el índice real en this.items
        const index = this.items.findIndex(
          (i) => i.product_id === item.product_id && i.delivery_status_id === item.delivery_status_id,
        );

        if (index !== -1) {
          const updatedCount = this.items[index].validatedCount + 1;
          // Usamos $set para forzar a Vue 2 a detectar el cambio reactivo en la vista
          this.$set(this.items[index], 'validatedCount', updatedCount);
        }
      }
    },

    decrementDispatchable(item) {
      if (item.validatedCount > 0) {
        const index = this.items.findIndex(
          (i) => i.product_id === item.product_id && i.delivery_status_id === item.delivery_status_id,
        );

        if (index !== -1) {
          const updatedCount = this.items[index].validatedCount - 1;
          this.$set(this.items[index], 'validatedCount', updatedCount);
        }
      }
    },

    validateDispatchableInput(item) {
      const index = this.items.findIndex(
        (i) => i.product_id === item.product_id && i.delivery_status_id === item.delivery_status_id,
      );
      if (index === -1) return;

      const val = parseInt(this.items[index].validatedCount, 10);

      if (this.items[index].validatedCount === '' || this.items[index].validatedCount === null || Number.isNaN(val)) {
        return;
      }

      if (val < 0) {
        this.$set(this.items[index], 'validatedCount', 0);
      }
    },

    handleDispatchableBlur(item) {
      const index = this.items.findIndex(
        (i) => i.product_id === item.product_id && i.delivery_status_id === item.delivery_status_id,
      );
      if (index === -1) return;

      const currentItem = this.items[index];
      const val = parseInt(currentItem.validatedCount, 10);

      if (currentItem.validatedCount === '' || currentItem.validatedCount === null || Number.isNaN(val)) {
        this.$set(this.items[index], 'validatedCount', 0);
        return;
      }

      if (val > currentItem.pending) {
        this.$set(this.items[index], 'validatedCount', currentItem.pending);
      }
    },
    async confirmDispatch() {
      if (!this.canConfirm) {
        Swal.fire({
          icon: 'warning',
          title: 'Faltan datos',
          text: 'Valida la identificación y asegúrate de entregar al menos un artículo.',
          confirmButtonColor: 'primary',
        });
        return;
      }

      const storeId = this.$store.getters.currentStoreId;

      const payload = {
        folio: Number(this.folioVenta),
        store_id: storeId,
        document_folio: this.documentFolio.trim(),
        notes: this.notes,
        timestamp: new Date().toISOString(),

        // Solo enviamos las partidas de la tabla despachable
        items: this.dispatchableItems
          .filter((item) => item.validatedCount > 0)
          .map((item) => ({
            product_id: item.product_id,
            validatedCount: Number(item.validatedCount),
          })),
      };

      try {
        this.isConfirming = true;
        this.isEntregado = true;

        const response = await ClientDeliveryService.confirmDelivery(payload);

        if (response.status === 'success') {
          Swal.fire({
            icon: 'success',
            title: '¡Despacho Exitoso!',
            text: response.message || 'El inventario ha sido actualizado correctamente.',
            confirmButtonColor: 'success',
            confirmButtonText: 'Aceptar',
          }).then(() => {
            this.volverBandeja();
          });
        }
      } catch (error) {
        this.isEntregado = false;
        const errorMessage = error.response?.data?.detail || 'No se pudo procesar la entrega. Intenta de nuevo.';

        Swal.fire({
          icon: 'error',
          title: 'Error de Despacho',
          text: errorMessage,
          confirmButtonColor: 'primary',
        });
      } finally {
        this.isConfirming = false;
      }
    },

    volverBandeja() {
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.custom-number-input >>> input {
  text-align: center;
  font-weight: bold;
}

.custom-number-input >>> input[type="number"]::-webkit-outer-spin-button,
.custom-number-input >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.custom-number-input >>> input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
